import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import useHttp from '../../hooks/http.hook'
import FullscreenSpinner from '../components/FullscreenSpinner'
import {
  buildProductsDetailsMap,
  cartItemsToOrderLines,
  computeMaxGiftUnitsForPromotion,
  evaluatePromotionConditions,
  unitPriceAfterDiscount,
} from '../../utils/orderPromotionConditions'
import { flattenBusinessProductGroups, mapBusinessProductGroups } from '../../utils/productGroups'
import { useTheme } from '../../context/theme.context'
import './GiftSelectionPage.css'

function rewardRowKey(reward, index) {
  const raw = reward?.id ?? reward?.Id
  if (raw != null) return String(raw)
  return `i-${index}`
}

function buildDraftOrderLines(cartItems, promotionId, quantities, rewardsWithProducts) {
  const base = cartItemsToOrderLines(cartItems).filter(
    (l) =>
      !(
        l.promotionId != null &&
        promotionId != null &&
        Number(l.promotionId) === Number(promotionId)
      )
  )
  const out = [...base]
  for (const row of rewardsWithProducts) {
    const q = quantities[row.rowKey] ?? 0
    if (q > 0) {
      out.push({
        productId: row.productId,
        quantity: q,
        promotionId,
      })
    }
  }
  return out
}

function sumQuantities(quantities) {
  return Object.values(quantities).reduce((s, n) => s + (Number(n) || 0), 0)
}

function mergeCartWithGiftQuantities(cartItems, quantities, rewardsWithProducts, promotion) {
  const m = new Map()
  for (const it of cartItems || []) {
    if (it?.key != null) m.set(it.key, it)
  }
  for (const [k, v] of [...m.entries()]) {
    if (v?.promotionId != null && Number(v.promotionId) === Number(promotion.id)) {
      m.delete(k)
    }
  }
  for (const row of rewardsWithProducts) {
    const qty = quantities[row.rowKey] ?? 0
    if (qty <= 0) continue
    const { reward, productId, product, rowKey } = row
    const base = typeof product.price === 'number' ? product.price : Number(product.price)
    const price = Number.isFinite(base) ? unitPriceAfterDiscount(base, reward.discount) : 0
    const key = `promo-${promotion.id}-r-${rowKey}`
    m.set(key, {
      key,
      id: productId,
      name: product.name ?? 'Товар',
      price,
      image: product.image,
      quantity: qty,
      promotionId: promotion.id,
    })
  }
  return [...m.values()]
}

function GiftSelectionPage() {
  const { business, promotionId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { requestWithMeta } = useHttp()
  const { applyBusinessColorType } = useTheme()

  const cartItems = useMemo(() => {
    const items = location.state?.cartItems
    return Array.isArray(items) ? items : []
  }, [location.state])

  const [promotion, setPromotion] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [fetchedProducts, setFetchedProducts] = useState([])
  /** rowKey -> quantity */
  const [quantities, setQuantities] = useState({})
  const [quantitiesInit, setQuantitiesInit] = useState(false)

  useEffect(() => {
    setQuantities({})
    setQuantitiesInit(false)
  }, [promotionId, business])

  const catalogProducts = useMemo(
    () => flattenBusinessProductGroups(fetchedProducts),
    [fetchedProducts]
  )

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!business || !promotionId) {
        setError('Неверная ссылка')
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError(null)
        const { ok, data, status } = await requestWithMeta(
          `/business/${encodeURIComponent(business)}/promotions/${encodeURIComponent(promotionId)}`
        )
        if (cancelled) return
        if (!ok) {
          setError(status === 404 ? 'Акция не найдена' : data?.error || 'Ошибка загрузки')
          setPromotion(null)
          return
        }
        setPromotion(data?.promotion || null)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Ошибка загрузки')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [business, promotionId, requestWithMeta])

  useEffect(() => {
    if (!business) return
    let cancelled = false
    ;(async () => {
      const businessRes = await requestWithMeta(`/business/${encodeURIComponent(business)}`)
      if (cancelled || !businessRes.ok) return
      applyBusinessColorType(business, businessRes.data?.business?.colorType)

      const all = []
      let page = 1
      let totalPages = 1
      do {
        const { ok, data } = await requestWithMeta(
          `/business/${encodeURIComponent(business)}/products?page=${page}&limit=100`
        )
        if (cancelled || !ok) return
        all.push(...mapBusinessProductGroups(data?.products))
        totalPages = Number(data?.pagination?.totalPages) || 0
        page += 1
      } while (page <= totalPages)
      if (!cancelled) setFetchedProducts(all)
    })()
    return () => {
      cancelled = true
    }
  }, [business, requestWithMeta, applyBusinessColorType])

  const productsDetailsMap = useMemo(() => buildProductsDetailsMap(catalogProducts), [catalogProducts])

  const productById = useMemo(() => {
    const m = new Map()
    for (const p of catalogProducts) {
      const id = p?.id ?? p?._id
      if (id != null) m.set(Number(id), p)
    }
    return m
  }, [catalogProducts])

  const rewardsWithProducts = useMemo(() => {
    const rewards = promotion?.rewards ?? []
    return rewards
      .map((r, index) => {
        const pid = r.productId ?? r.product_id
        const n = Number(pid)
        const product = Number.isFinite(n) ? productById.get(n) : null
        const rk = rewardRowKey(r, index)
        return { reward: r, productId: n, product, rowKey: rk, index }
      })
      .filter((x) => x.product)
  }, [promotion, productById])

  useEffect(() => {
    if (!promotion || rewardsWithProducts.length === 0 || quantitiesInit) return
    const init = {}
    for (const row of rewardsWithProducts) {
      const key = `promo-${promotion.id}-r-${row.rowKey}`
      const line = cartItems.find((it) => it.key === key)
      init[row.rowKey] = line?.quantity ?? 0
    }
    setQuantities(init)
    setQuantitiesInit(true)
  }, [promotion, rewardsWithProducts, cartItems, quantitiesInit])

  const baseLinesForCap = useMemo(
    () =>
      cartItemsToOrderLines(cartItems).filter(
        (l) =>
          !(
            l.promotionId != null &&
            promotion?.id != null &&
            Number(l.promotionId) === Number(promotion.id)
          )
      ),
    [cartItems, promotion?.id]
  )

  const maxGiftUnits = useMemo(
    () =>
      promotion
        ? computeMaxGiftUnitsForPromotion(promotion, baseLinesForCap, productsDetailsMap)
        : 0,
    [promotion, baseLinesForCap, productsDetailsMap]
  )

  const effectiveMaxGiftUnits = Number.isFinite(maxGiftUnits) ? maxGiftUnits : 999

  const draftOrderLines = useMemo(
    () => buildDraftOrderLines(cartItems, promotion?.id, quantities, rewardsWithProducts),
    [cartItems, promotion?.id, quantities, rewardsWithProducts]
  )

  const conditionEval = useMemo(
    () =>
      evaluatePromotionConditions(promotion?.conditions ?? [], {
        products: draftOrderLines,
        productsDetailsMap,
        promoCode: '',
        promotionId: promotion?.id,
      }),
    [promotion, draftOrderLines, productsDetailsMap]
  )

  const totalQty = useMemo(() => sumQuantities(quantities), [quantities])
  const atGiftLimit = totalQty >= effectiveMaxGiftUnits && effectiveMaxGiftUnits >= 0

  const bumpQty = useCallback(
    (rowKey, delta) => {
      setQuantities((prev) => {
        const cur = prev[rowKey] ?? 0
        const next = { ...prev }
        const total = sumQuantities(prev)
        if (delta > 0) {
          if (total >= effectiveMaxGiftUnits) return prev
          next[rowKey] = cur + 1
          return next
        }
        if (cur <= 0) return prev
        next[rowKey] = cur - 1
        if (next[rowKey] <= 0) delete next[rowKey]
        return next
      })
    },
    [effectiveMaxGiftUnits]
  )

  const handleNext = useCallback(() => {
    if (!business || !promotion || totalQty === 0 || !conditionEval.ok) return
    const mergedLines = mergeCartWithGiftQuantities(cartItems, quantities, rewardsWithProducts, promotion)
    const cartRecord = Object.fromEntries(mergedLines.map((l) => [l.key, l]))
    navigate(`/${encodeURIComponent(business)}`, {
      state: { openCartWithMerged: cartRecord },
    })
  }, [business, promotion, totalQty, conditionEval.ok, cartItems, quantities, rewardsWithProducts, navigate])

  return (
    <div className="gift-selection-page">
      <FullscreenSpinner open={loading} label="Загрузка…" />

      <div className="gift-selection-inner">
        <div className="gift-selection-header">
          <div className="gift-selection-title">Выберите подарки</div>
        </div>

        <div className="gift-selection-body">
          {error && <div className="gift-selection-error">{error}</div>}

          {!loading && !error && promotion && (
            <>
              <div className="gift-selection-promo-name">{promotion.name}</div>
              {Number.isFinite(maxGiftUnits) && (
                <div className="gift-selection-counter">
                  Подарков по условию: {totalQty} / {maxGiftUnits}
                  {atGiftLimit && (
                    <span className="gift-selection-counter-limit"> — достигнут лимит по акции</span>
                  )}
                </div>
              )}
              {!Number.isFinite(maxGiftUnits) && totalQty > 0 && (
                <div className="gift-selection-counter">Выбрано единиц: {totalQty}</div>
              )}
              {!conditionEval.ok && (
                <div className="gift-selection-condition-msg" role="status">
                  {conditionEval.error}
                </div>
              )}

              {rewardsWithProducts.length === 0 ? (
                <div className="gift-selection-empty">Для этой акции нет подарков.</div>
              ) : (
                <ul className="gift-selection-list">
                  {rewardsWithProducts.map((row) => {
                    const q = quantities[row.rowKey] ?? 0
                    const plusDisabled = !conditionEval.ok || atGiftLimit
                    const minusDisabled = q <= 0
                    const { product, reward } = row
                    const base = typeof product.price === 'number' ? product.price : Number(product.price)
                    const unit = Number.isFinite(base) ? unitPriceAfterDiscount(base, reward.discount) : 0
                    return (
                      <li
                        key={row.rowKey}
                        className={`gift-selection-item ${q > 0 ? 'gift-selection-item--picked' : ''}`}
                      >
                        {product.image ? (
                          <img className="gift-selection-img" src={product.image} alt="" />
                        ) : (
                          <div className="gift-selection-img gift-selection-img--placeholder" />
                        )}
                        <div className="gift-selection-meta">
                          <div className="gift-selection-item-name">{product.name}</div>
                          <div className="gift-selection-item-price">
                            {unit <= 0 ? 'Бесплатно' : `${unit.toFixed(2)} сом / шт.`}
                          </div>
                        </div>
                        <div className="gift-selection-qty" aria-label="Количество">
                          <button
                            type="button"
                            className="gift-selection-qty-btn"
                            disabled={minusDisabled}
                            onClick={() => bumpQty(row.rowKey, -1)}
                            aria-label="Уменьшить"
                          >
                            −
                          </button>
                          <span className="gift-selection-qty-value">{q}</span>
                          <button
                            type="button"
                            className="gift-selection-qty-btn"
                            disabled={plusDisabled}
                            onClick={() => bumpQty(row.rowKey, 1)}
                            aria-label="Увеличить"
                          >
                            +
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </>
          )}
        </div>

        {!loading && !error && promotion && rewardsWithProducts.length > 0 && (
          <div className="gift-selection-footer">
            <button
              type="button"
              className="gift-selection-next"
              disabled={totalQty === 0 || !conditionEval.ok}
              onClick={handleNext}
            >
              Далее
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default GiftSelectionPage
